import { useState, useEffect, useContext } from "react";
import {
  Settings,
  Clock,
  Calendar,
  Save,
  RotateCcw,
  TrendingUp,
  Building,
  Plus,
  X,
  DollarSign,
  Search,
  Fingerprint,
  Bell,
  GitBranch,
  Info,
} from "lucide-react";

import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { useProfile } from "../../hooks/useProfile";
import { getProfileFirstName } from "../../utils/profileIdentity";

// ─── Hooks ───────────────────────────────────────────────────────────────────
import usePermissions   from "./hooks/usePermissions";
import useConfiguration from "./hooks/useConfiguration";
import useArchives      from "./hooks/useArchives";
import useDepartments   from "./hooks/useDepartments";

// ─── Tab Components ───────────────────────────────────────────────────────────
import CompanyTab      from "./components/tabs/CompanyTab";
import AttendanceTab   from "./components/tabs/AttendanceTab";
import OvertimeTab     from "./components/tabs/OvertimeTab";
import LeavesTab       from "./components/tabs/LeavesTab";
import AdvancementsTab from "./components/tabs/AdvancementsTab";
import PayrollDatesTab from "./components/tabs/PayrollDatesTab";
import FingerprintTab  from "./components/tabs/FingerprintTab";
import NotificationsTab from "./components/tabs/NotificationsTab";
import WorkflowsTab from "./components/tabs/WorkflowsTab";

// ─── Modal ────────────────────────────────────────────────────────────────────
import CompanyModal from "./components/modals/CompanyModal";
import FingerprintDetailsModal from "./components/modals/FingerprintDetailsModal";
import GlobalEffectiveDateBar from "./components/GlobalEffectiveDateBar";

// ─── Styles & Translations ────────────────────────────────────────────────────
import { getStyles, PALETTE }  from "./styles/configStyles";
import text           from "./constants/translations";

// ─── Components ──────────────────────────────────────────────────────────────
import WelcomeCard from "../../components/WelcomeCard";
import SaveSuccessModal from "../../components/SaveSuccessModal";
import FailureModal from "../../components/FailureModal";

// ─── External ────────────────────────────────────────────────────────────────
import AlertModal from "../../components/AlertModal";
import { apiFetch } from "../../context/AuthContext";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

// =============================================================================
const CompanyConfiguration = () => {
  const { theme }    = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile }  = useProfile();
  const BASE_URL     = process.env.REACT_APP_BASE_URL;

  const t     = text[language] || text.en;
  const isRTL = language === "ar";

  // ── Derived styles (re-computed when theme / direction changes) ──────────────
  const styles = getStyles(theme, isRTL);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [activeTab,                 setActiveTab]                 = useState("company");
  const [showCompanyModal,          setShowCompanyModal]          = useState(false);
  const [showCreateModal,           setShowCreateModal]           = useState(false);
  const [selectedFingerprintMode,   setSelectedFingerprintMode]   = useState(null);
  const [detectingCompanies,        setDetectingCompanies]        = useState(false);
  const [saveFailed,                setSaveFailed]                = useState(false);
  const [alertModal,                setAlertModal]                = useState({ open: false, title: "", message: "", variant: "info" });

  const showAlert = (title, message, variant = "info") =>
    setAlertModal({ open: true, title, message, variant });
  const closeAlert = () =>
    setAlertModal((prev) => ({ ...prev, open: false }));

  // ── Permissions ──────────────────────────────────────────────────────────────
  const {
    isAdmin,
    modulePermissions,
    loadingPermissions,
    canEditField: _canEditField,
  } = usePermissions({ BASE_URL, profile });

  // canEditField needs to know about isEditingArchive, but that comes from
  // useArchives below — we wire it up after both hooks are initialised.
  // We use a thin wrapper defined after the archives hook.

  // ── Configuration ────────────────────────────────────────────────────────────
  const {
    config,
    setConfig,
    loading,
    setLoading,
    saveSuccess,
    selectedCompanyName,
    setSelectedCompanyName,
    detectedCompanies,
    setDetectedCompanies,
    isCreatingNewConfig,
    setIsCreatingNewConfig,
    loadConfiguration,
    persistConfiguration,
    globalEffectiveDate,
    setGlobalEffectiveDate,
    unlockedTabs,
    setUnlockedTabs,
    handleGlobalDateChange,
    unlockTab,
    resetTabToGlobal,
    getTabEffectiveDate,
    getBadgeMode,
  } = useConfiguration({ BASE_URL, profile });

  // ── Archives ─────────────────────────────────────────────────────────────────
  const {
    archivedConfigs,
    archivesLoading,
    selectedArchiveId,
    setSelectedArchiveId,
    isEditingArchive,
    setIsEditingArchive,
    loadArchives,
    selectArchive,
    updateArchive,
  } = useArchives({
    BASE_URL,
    profile,
    selectedCompanyName,
    setConfig,
    setActiveTab,
  });

  // ── Permission wrapper (archive-aware) ───────────────────────────────────────
  const canEditField = (section) => _canEditField(section, isEditingArchive);

  const getFieldStyle = (section, baseStyle) => {
    if (isAdmin) return baseStyle;
    const isEditable = canEditField(section);
    return {
      ...baseStyle,
      opacity:    isEditable ? 1 : 0.7,
      cursor:     isEditable ? "text" : "not-allowed",
      background: isEditable
        ? baseStyle.background
        : theme === "dark"
          ? "rgba(51, 65, 85, 0.3)"
          : "rgba(241, 245, 249, 0.5)",
    };
  };

  // ── Departments ──────────────────────────────────────────────────────────────
  const {
    departments,
    loadingDepartments,
    fetchDepartmentsAndCompanies,
  } = useDepartments({
    BASE_URL,
    profile,
    selectedCompanyName,
    setConfig,
    showAlert,
    isRTL,
  });

  // ── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCompanyName) {
      fetchDepartmentsAndCompanies();
      loadArchives(selectedCompanyName); // eslint-disable-line react-hooks/exhaustive-deps
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyName]);

  // ── Initial-load skeleton — only until the first successful load ─────────────
  const isInitialLoading = loading || loadingPermissions;
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) setPageReady(true);
  }, [isInitialLoading]);

  // ── Detect / select companies ─────────────────────────────────────────────────
  const handleDetectCompanies = async (autoCreate = false) => {
    try {
      setDetectingCompanies(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName") || "Infinity";
      const url = `${BASE_URL}/api/company-config/${clientName}/detect-companies${
        autoCreate ? "?autoCreate=true" : ""
      }`;

      const response = await apiFetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      const data = await response.json();

      if (data.success) {
        const companies = (data.data.companies || []).map((c) => ({
          ...c,
          isConfigured: c.hasConfiguration || c.isConfigured || false,
        }));
        setDetectedCompanies(companies);
        if (data.data.departmentRestrictions) {
          window.departmentRestrictions = data.data.departmentRestrictions;
        }
        setShowCompanyModal(true);
        if (autoCreate && data.data.created?.length > 0) {
          showAlert(
            isRTL ? "نجاح" : "Success",
            isRTL
              ? `تم إنشاء ${data.data.created.length} إعدادات بنجاح!`
              : `Successfully created ${data.data.created.length} configurations!`,
            "success"
          );
          handleDetectCompanies(false);
        }
      } else {
        showAlert(
          isRTL ? "خطأ" : "Error",
          (isRTL ? "فشل في اكتشاف الشركات: " : "Failed to detect companies: ") + data.message,
          "error"
        );
      }
    } catch (err) {
      console.error("Error detecting companies:", err);
      showAlert(
        isRTL ? "خطأ" : "Error",
        isRTL ? "فشل في اكتشاف الشركات" : "Failed to detect companies",
        "error"
      );
    } finally {
      setDetectingCompanies(false);
    }
  };

  const handleSelectCompany = async (companyName) => {
    setSelectedCompanyName(companyName);
    setShowCompanyModal(false);
    await loadConfiguration(companyName);
  };

  // ── Save / create ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    await persistConfiguration({
      createNewVersion: false,
      isEditingArchive,
      selectedArchiveId,
      updateArchive: (id) => updateArchive(id, config, showAlert, loadConfiguration, prepareAdvancementConfigForSave),
      showAlert,
      loadArchives,
      isRTL,
      t,
    });
  };

  // We need prepareAdvancementConfigForSave here for the archive update path.
  // Import it from utils.
  const { prepareAdvancementConfigForSave } = require("./utils/advancementUtils");

  const handleCreateConfig = async () => {
    if (isEditingArchive) {
      showAlert(
        isRTL ? "تنبيه" : "Warning",
        isRTL ? "اخرج من وضع تعديل الأرشيف أولاً" : "Exit archive edit mode first",
        "warning"
      );
      return;
    }
    if (!isCreatingNewConfig) {
      setIsCreatingNewConfig(true);
      setShowCreateModal(true);
      // Initialize global effective date to today
      const today = new Date().toISOString().split("T")[0];
      setGlobalEffectiveDate(today);
      setConfig((prev) => ({
        ...prev,
        attendanceEffectiveDate: today,
        overtimeEffectiveDate: today,
        leaveEffectiveDate: today,
        advancementEffectiveDate: today,
      }));
      // Reset unlock state
      setUnlockedTabs({
        attendance: false,
        overtime: false,
        leave: false,
        advancement: false,
      });
      return;
    }
    await persistConfiguration({
      createNewVersion: true,
      isEditingArchive,
      selectedArchiveId,
      updateArchive: (id) => updateArchive(id, config, showAlert, loadConfiguration, prepareAdvancementConfigForSave),
      showAlert,
      loadArchives,
      isRTL,
      t,
    });
    setIsCreatingNewConfig(false);
    setShowCreateModal(false);
  };

  const handleReset = () => {
    if (window.confirm(t.confirmReset)) {
      loadConfiguration(selectedCompanyName);
    }
  };

  // ── Shared props passed down to every tab ─────────────────────────────────────
  const sharedTabProps = {
    styles,
    t,
    theme,
    isRTL,
    language,
    config,
    setConfig,
    canEditField,
    getFieldStyle,
    canManageDepartmentRules: Boolean(modulePermissions?.manageDepartmentRules),
    isCreatingNewConfig,
    isEditingArchive,
    setIsEditingArchive,
    selectedArchiveId,
    setSelectedArchiveId,
    archivedConfigs,
    selectArchive,
    onOpenFingerprintDetails: setSelectedFingerprintMode,
    unlockTab,
    resetTabToGlobal,
    getTabEffectiveDate,
    getBadgeMode,
  };

  // ── Tab definitions ───────────────────────────────────────────────────────────
  const TABS = [
    { id: "company",      label: t.tabs.companyInfo,   icon: Building,   permission: "viewCompanyInfo"  },
    { id: "attendance",   label: t.tabs.attendance,    icon: Clock,      permission: "viewAttendance"   },
    { id: "overtime",     label: t.tabs.overtime,      icon: TrendingUp, permission: "viewOvertime"     },
    { id: "leaves",       label: t.tabs.leaves,        icon: Calendar,   permission: "viewLeaves"       },
    { id: "advancements", label: t.tabs.advancements,  icon: DollarSign, permission: "viewAdvancements" },
    { id: "payrollDates", label: t.tabs.payrollDates,  icon: Calendar,   permission: "viewCompanyInfo"  },
    { id: "fingerprint",  label: t.tabs.fingerprint,      icon: Fingerprint, permission: "viewAttendance"  },
    { id: "notifications", label: t.tabs.notifications || (language === "ar" ? "الإشعارات" : "Notifications"), icon: Bell, permission: "viewLeaves" },
    { id: "workflows", label: t.tabs.workflows || (language === "ar" ? "سير العمل" : "Workflows"), icon: GitBranch, permission: "viewLeaves" },
  ].filter((tab) => modulePermissions[tab.permission]);

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isInitialLoading) {
    return (
      <div>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات إعدادات الشركة..." : "Loading company configuration..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Header skeleton */}
        <div style={styles.contentSection}>
          <SkeletonBox width="240px" height="30px" style={{ marginBottom: "10px" }} />
          <SkeletonBox width="340px" height="18px" />
        </div>

        {/* Tabs skeleton */}
        <div style={{ ...styles.contentSection, paddingBottom: 0, paddingTop: "12px" }}>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {["company", "attendance", "overtime", "leaves", "advancements", "payrollDates", "fingerprint", "notifications"].map((tab) => (
              <SkeletonBox key={tab} width="120px" height="40px" style={{ borderRadius: "8px 8px 0 0" }} />
            ))}
          </div>
        </div>

        {/* Active tab content skeleton */}
        <div style={styles.contentSection}>
          <SkeletonBox width="220px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={styles.formRow}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={styles.formGroup}>
                <SkeletonBox width="120px" height="14px" />
                <SkeletonBox width="100%" height="42px" style={{ borderRadius: "12px" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="nx-config-page">

      {/* ===== HEADER (using WelcomeCard for consistent styling) ===== */}
      <WelcomeCard
        styles={styles}
        text={text}
        language={language}
        userName={getProfileFirstName(profile, language)}
        title={{ en: t.pageTitle, ar: t.pageTitle }}
        subtitle={t.pageSubtitle}
        extraContent={
          selectedCompanyName && (
            <div 
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 12px",
                borderRadius: "6px",
                background: theme === "dark" ? "#0f2847" : "#f0f9ff",
                border: `1px solid ${theme === "dark" ? "#164e63" : "#0ea5e9"}`,
                fontSize: "13px",
                fontWeight: 500,
                color: theme === "dark" ? "#7dd3fc" : "#0369a1",
                flexDirection: "row",
                transition: "all 0.2s ease",
                animation: "ccSlideUp 0.4s ease-out",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme === "dark" ? "#06b6d4" : "#0284c7";
                e.currentTarget.style.background = theme === "dark" ? "#164e63" : "#ecf8ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme === "dark" ? "#164e63" : "#0ea5e9";
                e.currentTarget.style.background = theme === "dark" ? "#0f2847" : "#f0f9ff";
              }}
            >
              {/* Status Indicator */}
              {!isRTL && (
                <span style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: theme === "dark" ? "#06b6d4" : "#0284c7",
                  flexShrink: 0,
                  boxShadow: theme === "dark" 
                    ? "0 0 8px rgba(6, 182, 212, 0.8), 0 0 16px rgba(6, 182, 212, 0.4)"
                    : "0 0 6px rgba(2, 132, 199, 0.6), 0 0 12px rgba(2, 132, 199, 0.3)",
                  animation: "ccStatusBeat 1.2s ease-in-out infinite",
                }} />
              )}
              
              {isRTL ? (
                <>
                  <span style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: theme === "dark" ? "#06b6d4" : "#0284c7",
                    flexShrink: 0,
                    boxShadow: theme === "dark" 
                      ? "0 0 8px rgba(6, 182, 212, 0.8), 0 0 16px rgba(6, 182, 212, 0.4)"
                      : "0 0 6px rgba(2, 132, 199, 0.6), 0 0 12px rgba(2, 132, 199, 0.3)",
                    animation: "ccStatusBeat 1.2s ease-in-out infinite",
                  }} />
                  <Building size={14} style={{ flexShrink: 0, color: theme === "dark" ? "#06b6d4" : "#0284c7" }} />
                  <span style={{ color: theme === "dark" ? "#7dd3fc" : "#0369a1" }}>
                    {t.currentlyConfiguring}
                  </span>
                  
                  <strong style={{ 
                    fontWeight: 600, 
                    color: theme === "dark" ? "#ffffff" : "#0c4a6e",
                    background: "transparent",
                    fontSize: "13px",
                  }}>
                    {selectedCompanyName}
                  </strong>
                </>
              ) : (
                <>
                  <Building size={14} style={{ flexShrink: 0, color: theme === "dark" ? "#06b6d4" : "#0284c7" }} />
                  <span style={{ color: theme === "dark" ? "#7dd3fc" : "#0369a1" }}>
                    {t.currentlyConfiguring}
                  </span>
                  <strong style={{ 
                    fontWeight: 600, 
                    color: theme === "dark" ? "#ffffff" : "#0c4a6e",
                    background: "transparent",
                    fontSize: "13px",
                  }}>
                    {selectedCompanyName}
                  </strong>
                </>
              )}
            </div>
          )
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: isRTL ? "flex-end" : "flex-start" }}>
         
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", flexDirection: "row" }}>
            {modulePermissions?.edit && (
              <button
                onClick={handleCreateConfig}
                disabled={loading}
                style={{
                  ...styles.detectButton,
                  background: `linear-gradient(135deg, ${PALETTE.emerald.base}, ${PALETTE.emerald.dark})`,
                  color: "white",
                  boxShadow: `0 4px 14px ${PALETTE.emerald.base}30`,
                }}
              >
                {!isRTL && <Plus size={20} />}
                {t.startCreateConfiguration}
                {isRTL  && <Plus size={20} />}
              </button>
            )}
            {modulePermissions?.detectCompanies && (
              <button
                onClick={() => handleDetectCompanies()}
                disabled={detectingCompanies}
                style={styles.detectButton}
              >
                {!isRTL && <Search size={20} />}
                {detectingCompanies ? t.detectingCompanies : t.detectCompanies}
                {isRTL  && <Search size={20} />}
              </button>
            )}
          </div>
        </div>
        
      </WelcomeCard>
      

      {/* ===== COMPANY MODAL ===== */}
      <CompanyModal
        styles={styles}
        t={t}
        isRTL={isRTL}
        theme={theme}
        detectedCompanies={detectedCompanies}
        detectingCompanies={detectingCompanies}
        showCompanyModal={showCompanyModal}
        setShowCompanyModal={setShowCompanyModal}
        handleSelectCompany={handleSelectCompany}
        handleDetectCompanies={handleDetectCompanies}
        canAutoCreate={Boolean(modulePermissions?.autoCreateConfigs)}
      />

      {/* ===== SAVE BAR (top of the page; scrolls away with the content) ===== */}
      <div
        style={{
          ...styles.contentSection,
          padding: "12px 16px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px 14px",
        }}
      >
        {/* Effective-date note */}
        <div
          style={{
            flex: "1 1 320px",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.4,
            border: isCreatingNewConfig
              ? "1px solid rgba(59,130,246,0.4)"
              : "1px solid rgba(148,163,184,0.3)",
            background: isCreatingNewConfig
              ? theme === "dark"
                ? "rgba(59,130,246,0.14)"
                : "rgba(219,234,254,0.8)"
              : theme === "dark"
                ? "rgba(148,163,184,0.1)"
                : "rgba(241,245,249,0.9)",
            color: isCreatingNewConfig
              ? theme === "dark" ? "#93c5fd" : "#1d4ed8"
              : theme === "dark" ? "rgba(226,232,240,0.85)" : "#475569",
          }}
        >
          <Info size={15} style={{ flexShrink: 0, opacity: 0.8 }} />
          <span>{isCreatingNewConfig ? t.effectiveDateBannerCreate : t.effectiveDateBannerDefault}</span>
        </div>

        <div style={{ ...styles.actionButtons, marginTop: 0, marginInlineStart: "auto", gap: "10px" }}>
          {/* Reset */}
          {modulePermissions?.reset && (
            <button onClick={handleReset} disabled={loading} style={{ ...styles.resetButton, padding: "11px 20px", fontSize: "14px" }}>
              {t.resetToDefault}
              <RotateCcw size={18} />
            </button>
          )}

          {/* Confirm create when in creation mode */}
          {modulePermissions?.edit && isCreatingNewConfig && (
            <button
              onClick={handleCreateConfig}
              disabled={loading}
              style={{
                ...styles.saveButton,
                padding: "11px 22px",
                fontSize: "14px",
                background: `linear-gradient(135deg, ${PALETTE.emerald.base} 0%, ${PALETTE.emerald.dark} 100%)`,
                boxShadow: `0 6px 20px ${PALETTE.emerald.base}30`,
              }}
            >
              {loading
                ? t.creatingConfiguration
                : t.confirmCreateConfiguration}
              <Plus size={18} />
            </button>
          )}

          {/* Cancel create */}
          {modulePermissions?.edit && isCreatingNewConfig && (
            <button
              onClick={() => setIsCreatingNewConfig(false)}
              disabled={loading}
              style={{ ...styles.resetButton, padding: "11px 20px", fontSize: "14px" }}
            >
              {t.cancelCreateConfiguration}
              <X size={18} />
            </button>
          )}

          {/* Save */}
          {modulePermissions?.edit && (
            <button
              onClick={handleSave}
              disabled={loading || isCreatingNewConfig}
              style={{
                ...styles.saveButton,
                padding: "11px 22px",
                fontSize: "14px",
                opacity: loading || isCreatingNewConfig ? 0.6 : 1,
                cursor: loading || isCreatingNewConfig ? "not-allowed" : "pointer",
              }}
            >
              {loading ? t.saving : t.saveConfiguration}
              <Save size={18} />
            </button>
          )}
        </div>
      </div>

      {/* ===== TABS BAR ===== */}
      <div style={{ ...styles.contentSection, paddingBottom: 0, paddingTop: "12px" }}>
        <div className="nx-tabs-scroll nx-config-tabs" style={styles.tabsContainer}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={styles.tab(activeTab === tab.id)}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== TAB CONTENT ===== */}
      <div
        style={{
          ...styles.contentSection,
          border: isEditingArchive
            ? "2px solid #f59e0b"
            : styles.contentSection.border,
          boxShadow: isEditingArchive
            ? theme === "dark"
              ? "0 0 0 4px rgba(245,158,11,0.18), 0 8px 24px rgba(0,0,0,0.3)"
              : "0 0 0 4px rgba(245,158,11,0.12), 0 4px 16px rgba(0,0,0,0.06)"
            : styles.contentSection.boxShadow,
          background: isEditingArchive
            ? theme === "dark"
              ? "rgba(245,158,11,0.04)"
              : "rgba(255,251,235,0.5)"
            : styles.contentSection.background,
        }}
      >
        {activeTab === "company" && (
          <CompanyTab
            {...sharedTabProps}
            selectedCompanyName={selectedCompanyName}
            detectedCompanies={detectedCompanies}
          />
        )}

        {activeTab === "attendance" && (
          <AttendanceTab
            {...sharedTabProps}
            selectedCompanyName={selectedCompanyName}
            departments={departments}
            loadingDepartments={loadingDepartments}
            loading={loading}
            setLoading={setLoading}
            loadConfiguration={loadConfiguration}
            updateArchive={(id) =>
              updateArchive(id, config, showAlert, loadConfiguration, prepareAdvancementConfigForSave)
            }
            loadArchives={loadArchives}
            BASE_URL={BASE_URL}
            profile={profile}
            showAlert={showAlert}
          />
        )}

        {activeTab === "overtime" && (
          <OvertimeTab {...sharedTabProps} />
        )}

        {activeTab === "leaves" && (
          <LeavesTab {...sharedTabProps} loading={loading} />
        )}

        {activeTab === "advancements" && (
          <AdvancementsTab {...sharedTabProps} />
        )}

        {activeTab === "payrollDates" && (
          <PayrollDatesTab {...sharedTabProps} />
        )}

        {activeTab === "fingerprint" && (
          <FingerprintTab {...sharedTabProps} />
        )}

        {activeTab === "notifications" && (
          <NotificationsTab {...sharedTabProps} />
        )}

        {activeTab === "workflows" && (
          <WorkflowsTab
            {...sharedTabProps}
            selectedCompanyName={selectedCompanyName}
            profile={profile}
            departments={departments}
            loadingDepartments={loadingDepartments}
            showAlert={showAlert}
          />
        )}
      </div>

      {/* ===== CSS ANIMATIONS ===== */}
      <style>{`
        @keyframes ccFadeIn    { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ccSlideUp   { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes ccSlideInUp { from { transform: translateY(100px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes ccStatusBeat {
          0%, 100% {
            transform: scale(1);
            box-shadow: ${theme === "dark"
              ? "0 0 8px rgba(6, 182, 212, 0.6), 0 0 16px rgba(6, 182, 212, 0.2)"
              : "0 0 6px rgba(2, 132, 199, 0.5), 0 0 12px rgba(2, 132, 199, 0.2)"};
          }
          50% {
            transform: scale(1.6);
            box-shadow: ${theme === "dark"
              ? "0 0 20px rgba(6, 182, 212, 1), 0 0 40px rgba(6, 182, 212, 0.8)"
              : "0 0 16px rgba(2, 132, 199, 0.9), 0 0 32px rgba(2, 132, 199, 0.6)"};
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        /* Spinner */
        .spinner {
          width: 20px;
          height: 20px;
          border: 3px solid rgba(99,102,241,0.2);
          border-top-color: rgba(99,102,241,1);
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        /* Input & Select Styles */
        .nx-config-page input:hover:not(:disabled), .nx-config-page select:hover:not(:disabled) {
          border-color: ${PALETTE.primary.base} !important;
        }
        .nx-config-page input:focus, .nx-config-page select:focus {
          outline: none;
          border-color: ${PALETTE.primary.base} !important;
          box-shadow: 0 0 0 3px ${PALETTE.primary.base}20 !important;
        }

        /* Button Styles */
        .nx-config-page button:hover:not(:disabled) { transform: translateY(-1px); }
        .nx-config-page button:active:not(:disabled) { transform: translateY(0); }
        .nx-config-page button:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Scrollbar */
        .nx-config-page ::-webkit-scrollbar { width: 8px; height: 8px; }
        .nx-config-page ::-webkit-scrollbar-track { background: ${theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"}; border-radius: 10px; }
        .nx-config-page ::-webkit-scrollbar-thumb { background: ${theme === "dark" ? "rgba(99,102,241,0.3)" : "rgba(99,102,241,0.2)"}; border-radius: 10px; }
        .nx-config-page ::-webkit-scrollbar-thumb:hover { background: ${theme === "dark" ? "rgba(99,102,241,0.5)" : "rgba(99,102,241,0.35)"}; }

        /* ===== RESPONSIVE DESIGN ===== */
        @media (max-width: 768px) {
          /* Prevent input zoom on iOS */
          .nx-config-page input, .nx-config-page select, .nx-config-page textarea {
            font-size: 16px !important;
          }

          /* Larger touch targets */
          .nx-config-page input:not([type="checkbox"]):not([type="radio"]), .nx-config-page select {
            padding: 12px 14px !important;
            min-height: 44px !important;
          }
          .nx-config-page button {
            min-height: 44px !important;
          }

          /* Full-width buttons on mobile */
          .nx-config-page button {
            width: 100% !important;
          }
          .nx-config-page .nx-config-tabs > button {
            width: auto !important;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          /* Tablet adjustments */
          .nx-config-page input, .nx-config-page select {
            font-size: 15px !important;
          }
        }

        /* Touch-friendly spacing for touch devices */
        @media (hover: none) and (pointer: coarse) {
          .nx-config-page button, .nx-config-page input:not([type="checkbox"]):not([type="radio"]), .nx-config-page select {
            min-height: 44px !important;
          }
        }
      `}</style>

      {/* ===== CREATE CONFIGURATION MODAL ===== */}
      {showCreateModal && isCreatingNewConfig && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateModal(false);
              setIsCreatingNewConfig(false);
            }
          }}
        >
          <div
            style={{
              background: theme === "dark" ? "#1e293b" : "#ffffff",
              borderRadius: "24px",
              width: "100%",
             maxWidth: "1200px",
              maxHeight: "95vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)"}`,
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "24px",
                borderBottom: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: "row",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexDirection: "row" }}>
                <Plus
                  size={28}
                  style={{
                    color: PALETTE.emerald.base,
                  }}
                />
                <div>
                  <h2
                    style={{
                      fontSize: "20px",
                      fontWeight: 800,
                      color: theme === "dark" ? "#f1f5f9" : "#0f172a",
                      margin: 0,
                    }}
                  >
                    {isRTL ? "إنشاء إعداد جديد" : "Create New Configuration"}
                  </h2>
                  {/* <p
                    style={{
                      fontSize: "13px",
                      color: theme === "dark" ? "rgba(226,232,240,0.7)" : "#64748b",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {isRTL
                      ? "قم بتعديل جميع الإعدادات أدناه"
                      : "Edit all settings below"}
                  </p> */}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setIsCreatingNewConfig(false);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: theme === "dark" ? "#94a3b8" : "#94a3b8",
                  padding: "0",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = theme === "dark" ? "rgba(148,163,184,0.1)" : "rgba(0,0,0,0.05)";
                  e.target.style.color = theme === "dark" ? "#f1f5f9" : "#0f172a";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = theme === "dark" ? "#94a3b8" : "#94a3b8";
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Tabs */}
            <div
              style={{
                position: "sticky",
                top: 0,
                padding: "0 24px",
                borderBottom: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)"}`,
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
                background: theme === "dark" ? "#1e293b" : "#ffffff",
                zIndex: 10,
                gap: "0",
              }}
            >
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: "16px 24px",
                    background: activeTab === tab.id
                      ? theme === "dark" ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)"
                      : "transparent",
                    border: "none",
                    borderBottom:
                      activeTab === tab.id
                        ? `3px solid ${PALETTE.primary.base}`
                        : "3px solid transparent",
                    fontSize: "14px",
                    fontWeight: activeTab === tab.id ? 700 : 600,
                    color:
                      activeTab === tab.id
                        ? theme === "dark" ? PALETTE.primary.light : PALETTE.primary.base
                        : theme === "dark" ? "#94a3b8" : "#64748b",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexDirection: "row",
                  }}
                >
                  <tab.icon size={22} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Modal Content */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "24px",
              }}
            >
              {/* Global Effective Date Bar */}
              <div style={{ marginBottom: "20px" }}>
                <GlobalEffectiveDateBar
                  globalDate={globalEffectiveDate}
                  onDateChange={handleGlobalDateChange}
                  isRTL={isRTL}
                  theme={theme}
                />
              </div>

              {activeTab === "company" && (
                <CompanyTab
                  {...sharedTabProps}
                  selectedCompanyName={selectedCompanyName}
                  detectedCompanies={detectedCompanies}
                />
              )}

              {activeTab === "attendance" && (
                <AttendanceTab
                  {...sharedTabProps}
                  selectedCompanyName={selectedCompanyName}
                  departments={departments}
                  loadingDepartments={loadingDepartments}
                  loading={loading}
                  setLoading={setLoading}
                  loadConfiguration={loadConfiguration}
                  updateArchive={(id) =>
                    updateArchive(id, config, showAlert, loadConfiguration, prepareAdvancementConfigForSave)
                  }
                  loadArchives={loadArchives}
                  BASE_URL={BASE_URL}
                  profile={profile}
                  showAlert={showAlert}
                />
              )}

              {activeTab === "overtime" && (
                <OvertimeTab {...sharedTabProps} />
              )}

              {activeTab === "leaves" && (
                <LeavesTab {...sharedTabProps} loading={loading} />
              )}

              {activeTab === "advancements" && (
                <AdvancementsTab {...sharedTabProps} />
              )}

              {activeTab === "payrollDates" && (
                <PayrollDatesTab {...sharedTabProps} />
              )}

              {activeTab === "fingerprint" && (
                <FingerprintTab {...sharedTabProps} />
              )}

              {activeTab === "notifications" && (
                <NotificationsTab {...sharedTabProps} />
              )}

              {activeTab === "workflows" && (
                <WorkflowsTab
                  {...sharedTabProps}
                  selectedCompanyName={selectedCompanyName}
                  profile={profile}
                  departments={departments}
                  loadingDepartments={loadingDepartments}
                  showAlert={showAlert}
                />
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "20px 24px",
                borderTop: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.8)"}`,
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                flexDirection: "row",
              }}
            >
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setIsCreatingNewConfig(false);
                }}
                disabled={loading}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: `1px solid ${theme === "dark" ? "#475569" : "#cbd5e1"}`,
                  background: theme === "dark" ? "transparent" : "transparent",
                  color: theme === "dark" ? "#f1f5f9" : "#0f172a",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = theme === "dark" ? "rgba(75,85,99,0.4)" : "rgba(0,0,0,0.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = theme === "dark" ? "transparent" : "transparent";
                }}
              >
                {isRTL ? "إلغاء" : "Cancel"}
              </button>
              <button
                onClick={handleCreateConfig}
                disabled={loading}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: loading
                    ? "#9ca3af"
                    : `linear-gradient(135deg, ${PALETTE.emerald.base}, ${PALETTE.emerald.dark})`,
                  color: "white",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.opacity = "0.9";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = "1";
                }}
              >
                {!isRTL && <Plus size={18} />}
                {loading ? t.creatingConfiguration : t.confirmCreateConfiguration}
                {isRTL && <Plus size={18} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ALERT MODAL ===== */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />

      {/* ===== FINGERPRINT DETAILS MODAL ===== */}
      <FingerprintDetailsModal
        selectedModeData={selectedFingerprintMode}
        theme={theme}
        isRTL={isRTL}
        language={language}
        onClose={() => setSelectedFingerprintMode(null)}
      />

      {/* ===== SUCCESS & FAILURE MODALS (fullscreen) ===== */}
      <SaveSuccessModal isOpen={saveSuccess} theme={theme} language={language} />
      <FailureModal isOpen={saveFailed} theme={theme} language={language} />
    </div>
  );
};

export default CompanyConfiguration;